/* Top bar rotator — sections/offer-bar.liquid.
 *
 * ES2019 only (FB/IG in-app WebViews) — no optional chaining / nullish
 * coalescing. Shopify re-minifies uploaded asset JS and rewrites
 * `x != null ? x : y` into `x ?? y`, so scripts/validate-es2019.mjs bans that
 * source shape too. `x || ''` is fine and is what this file uses.
 *
 * THE PASS IS BOUNDED, AND THAT IS THE FEATURE. Each page view performs
 * exactly `msgs.length - 1` transitions and then stops for good, so every live
 * message is shown once and none is shown twice. Removing the bound turns this
 * back into perpetual auto-updating content with no way for a touch user to
 * stop it, which is the WCAG 2.2.2 Level A failure the bound exists to avoid.
 * See docs/adr/0002-the-top-bar-stops-on-its-own.md before "fixing" the stop.
 *
 * THE TIMER ONLY RUNS WHEN THE BAR CAN BE SEEN. The header is not sticky
 * (header_sticky: false), so the bar leaves the viewport within a screen of
 * scrolling and does not come back. Advancing through a bar nobody is looking
 * at spends the pass on nothing and returns her to a message she never saw
 * arrive. Gated on IntersectionObserver and on document visibility.
 *
 * OWNERSHIP OF THE LINK FLIPS AT THE MIDPOINT. The whole bar is one <a>, and a
 * tap must always go to the message being LOOKED AT, never the one arriving.
 * The incoming message crosses the outgoing one at 50% of the travel, so that
 * is the first instant it owns the href — and the same instant it becomes the
 * one message exposed to assistive tech.
 *
 * NO CONTROL, BY DESIGN. A <button> cannot live inside an <a>, and the bar is
 * one <a> because at 390px a 35px bar is a 35px tap target only if all of it
 * is tappable. Keyboard users get the documented mechanism instead: the timer
 * freezes while the link has focus.
 */
(function () {
  'use strict';

  var HOLD = 5000;
  var DUR = 420;

  var bar = document.querySelector('[data-offer-bar]');
  if (!bar) return;

  var msgs = bar.querySelectorAll('.offer-bar__m');
  if (msgs.length < 2) return;

  // Reduced motion is a still bar on whichever message THIS page view opened
  // with — not always the first in the list, or a reduced-motion visitor would
  // be the only one who never sees the second and third messages. The inline
  // script in the section has already chosen it; this just never moves.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var idx = 0;
  for (var i = 0; i < msgs.length; i++) {
    if (msgs[i].className.indexOf('is-on') > -1) {
      idx = i;
      break;
    }
  }

  var left = msgs.length - 1;
  var seen = false;
  var focused = false;
  var timer = null;
  var io = null;

  function own(el) {
    var href = el.getAttribute('data-href') || '';
    if (href) bar.setAttribute('href', href);
    else bar.removeAttribute('href');
  }

  function halt() {
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
  }

  function tick() {
    if (timer || left <= 0) return;
    if (!seen || focused || document.hidden) return;
    timer = setInterval(advance, HOLD);
  }

  function done() {
    halt();
    if (io) {
      io.disconnect();
      io = null;
    }
  }

  function advance() {
    var prev = msgs[idx];
    var next = (idx + 1) % msgs.length;

    // Everything that is not the outgoing message is snapped back to its
    // starting position with the transition OFF. Without this the message
    // parked at -105% from the previous step slides down into place, which
    // reads as the rotation reversing direction.
    for (var j = 0; j < msgs.length; j++) {
      var el = msgs[j];
      if (el === prev) continue;
      if (el.className.indexOf('is-') > -1) {
        el.style.transition = 'none';
        el.className = 'offer-bar__m';
        void el.offsetWidth;
        el.style.transition = '';
      }
    }

    prev.className = 'offer-bar__m is-out';
    msgs[next].className = 'offer-bar__m is-on';
    idx = next;

    // The href and the accessible name move together, at the midpoint, so
    // exactly one message is ever exposed and it is always the visible one.
    setTimeout(function () {
      prev.setAttribute('aria-hidden', 'true');
      msgs[idx].removeAttribute('aria-hidden');
      own(msgs[idx]);
    }, DUR / 2);

    left -= 1;
    if (left <= 0) done();
  }

  if (typeof IntersectionObserver === 'function') {
    io = new IntersectionObserver(function (entries) {
      seen = entries[0].isIntersecting;
      if (seen) tick();
      else halt();
    });
    io.observe(bar);
  } else {
    seen = true;
    tick();
  }

  document.addEventListener('visibilitychange', function () {
    if (document.hidden) halt();
    else tick();
  });

  // Freeze on focus. This is the keyboard user's WCAG 2.2.2 mechanism and is
  // not decoration: without it the link they have tabbed to silently becomes a
  // different link under them.
  bar.addEventListener('focusin', function () {
    focused = true;
    halt();
  });
  bar.addEventListener('focusout', function () {
    focused = false;
    tick();
  });
})();
